<script lang="ts">
import {defineComponent} from 'vue'
import Card from 'primevue/card'
import ListBox from 'primevue/listbox'
import InputText from 'primevue/inputtext'
import {Course} from "@/interfaces/course"
import {fetchWrapper} from "@/helpers/fetchWrapper"
import backendRoutes from "@/backendRoutes"
import {ApiPlatformParser} from "@/helpers/apiPlatformParser"
import Textarea from "primevue/textarea"
import Checkbox from "primevue/checkbox"
import Button from "primevue/button"
import {genderOptions, Student} from "@/interfaces/student"
import {countryOptions} from "@/interfaces/address"
import Select from "primevue/select"
import DatePicker from "primevue/datepicker"
import {useThemeStore} from "@/stores/theme.store"
import formatApiDate from "@/helpers/formatApiDate"
import {ContactPerson} from "@/interfaces/contactperson"
import TelInput from "@/components/Global/Form/TelInput.vue"
import isNull from "@/helpers/isNull"
import FormField from "@/components/Global/Form/FormField.vue"
import {Workshop} from "@/interfaces/workshop"
import {LocationQueryValue} from "vue-router"
import priceFormat from "@/helpers/priceFormat"
import DataTable from "primevue/datatable"
import Column from "primevue/column"
import Stepper from "primevue/stepper"
import StepList from "primevue/steplist"
import Step from "primevue/step"
import StepPanels from "primevue/steppanels"
import StepPanel from "primevue/steppanel"
import Message from "primevue/message"
import {Payment, typeRegistration} from "@/interfaces/payment"
import PaymentProcessor from "@/components/Global/Payment/PaymentProcessor.vue";

export default defineComponent({
  name: "PublicRegisterView",
  components: {
    PaymentProcessor,
    FormField,
    TelInput,
    Card,
    ListBox,
    InputText,
    Textarea,
    Checkbox,
    Button,
    Select,
    DatePicker,
    DataTable,
    Column,
    StepPanels,
    StepPanel,
    Stepper,
    StepList,
    Step,
    Message,
  },
  data() {
    return {
      courses: [] as Course[],
      selectedCourses: [] as Course[],
      workshops: [] as Workshop[],
      selectedWorkshops: [] as Workshop[],
      student: {
        address: {},
        contactPeople: [
          {},
          {}
        ],
        courseRegistrations: [],
        workshopRegistrations: []
      } as Student,
      tryToGo: 1 as number,
      genderOptions: genderOptions,
      countryOptions: countryOptions,
      gdprConsent: false as boolean,
      rulesConsent: false as boolean,
      hasEdit: false as boolean,
      themeStore: useThemeStore(),
      contactPerson1: {} as ContactPerson,
      contactPerson2: {} as ContactPerson,
      saved: false as boolean,
      logoDark: process.env.VUE_APP_LOGO_DARK,
      logoLight: process.env.VUE_APP_LOGO_LIGHT,
      courseSelectFilter: [
          "Junior", "Jazz", "Classique", "Ragga", "Hip Hop", "Contempo", "Girly", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"
      ] as string[],
      selectFilters: "" as string,
      registerTypeOptions: ["course","workshop","both"] as string[],
      registerType: null as string | null,
      annualInsurancePrice: 0 as number,
      isStudentExisting: false as boolean,
      hasActiveInsuranceSubscription: false as boolean,
      activeStep: 1 as number,
      payment: null as Payment | null,
      allSuccess: false as boolean,
      typeRegistration: typeRegistration,
      canRegister: true as boolean
    }
  },
  watch: {
    "student.birthday": function () {
      if(this.student === undefined || this.student === null ||
          this.student.birthday === undefined || this.student.birthday === null) return;
      this.student.birthday = formatApiDate(this.student.birthday)
    },
    "registerType": function(newVal: string, oldVal: string) {
      if(oldVal == "both" && newVal == "course") this.student.workshopRegistrations = []
      if(oldVal == "both" && newVal == "workshop") this.student.courseRegistrations = []
      if(oldVal == "course" && newVal == "workshop") this.student.courseRegistrations = []
      if(oldVal == "workshop" && newVal == "course") this.student.workshopRegistrations = []
    }
  },
  mounted() {
    this.checkIfStudentCanRegister().then(() => {
      if(this.canRegister) {
        this.fetchCourseList()
        this.fetchWorkshopList()
        if (this.urlSelectedWorkShops.length > 0 && this.urlSelectedCourses.length > 0) this.registerType = "both"
        else if (this.urlSelectedWorkShops.length > 0) this.registerType = "workshop"
        else if (this.urlSelectedCourses.length > 0) this.registerType = "course"
      }
    })
  },
  computed: {
    age(): number {
      if (this.student?.birthday) {
        const parts = this.student.birthday.toString().split('/')
        const formattedBirthday = `${parts[2]}-${parts[1]}-${parts[0]}`
        const diff = Date.now() - new Date(formattedBirthday).getTime()
        const ageDate = new Date(diff)
        return Math.abs(ageDate.getUTCFullYear() - 1970)
      }
      return -1
    },
    isContactPeopleRequired(): boolean {
      return this.age < 18
    },
    urlSelectedWorkShops() : string[] {
      if(this.$route.query.workshops && this.$route.query.workshops instanceof Array) {
        let queries: LocationQueryValue[] = this.$route.query.workshops
        let strings: string[] = [];
        queries.forEach((workshop: LocationQueryValue) => {
          if(workshop) strings.push(workshop.toString())
        })
        return strings
      }else if (this.$route.query.workshops) return [this.$route.query.workshops.toString()]
      else return []
    },
    urlSelectedCourses(): string[] {
      if(this.$route.query.courses && this.$route.query.courses instanceof Array) {
        let queries: LocationQueryValue[] = this.$route.query.courses
        let strings: string[] = [];
        queries.forEach((course: LocationQueryValue) => {
          if(course) strings.push(course.toString())
        })
        return strings
      }else if (this.$route.query.courses) return [this.$route.query.courses.toString()]
      else return []
    },
    paymentTable(): Array<{name: string, price: number|string, bold?: boolean}> {
      let table = []
      let needInsurance = false
      let total = 0
      if(this.student.courseRegistrations) {
        this.student.courseRegistrations.forEach((course: Course) => {
          needInsurance = true;
          table.push({name: this.$t("student_views.public_register_view.messages.payment_table.course_registration")+ " " + course.name, price: this.$t("student_views.public_register_view.messages.payment_table.paid_by_access_card")})
        })
      }
      if(this.student.workshopRegistrations) {
        this.student.workshopRegistrations.forEach((workshop: Workshop) => {
          if (workshop.needInsurance) needInsurance = true;
          table.push({name: this.$t("student_views.public_register_view.messages.payment_table.workshop_registration")+ " " + workshop.name, price: workshop.price})
          if(workshop.price) total += workshop.price
        })
      }
      if(needInsurance){
        if(this.hasActiveInsuranceSubscription){
          table.push({
            name: this.$t("student_views.public_register_view.messages.payment_table.annual_insurance"),
            price: this.$t("student_views.public_register_view.messages.payment_table.has_active_subscription")
          })
        }else {
          table.push({
            name: this.$t("student_views.public_register_view.messages.payment_table.annual_insurance"),
            price: this.annualInsurancePrice
          })
          total += this.annualInsurancePrice
        }
      }
      table.push({name: this.$t("student_views.public_register_view.messages.payment_table.total"), price: total, bold: true})
      return table
    },
    paymentItems(): object {
      let needInsurance = false

      if(this.student.workshopRegistrations) {
        this.student.workshopRegistrations.forEach((workshop: Workshop) => {
          if (workshop.needInsurance) needInsurance = true;
        })
      }

      if(this.student.courseRegistrations && this.student.courseRegistrations?.length > 0) needInsurance = true
      return {
        courses: this.student.courseRegistrations,
        workshops: this.student.workshopRegistrations,
        insurance: needInsurance ? !this.hasActiveInsuranceSubscription : false
      }
    },
    totalPrice(): number{
      let price = 0
      this.paymentTable.forEach((item) => {
        if(item.bold && typeof item.price == "number") price =  item.price
      })
      return price
    }
  },
  methods: {
    isNull,
    priceFormat,
    async checkIfStudentCanRegister() {
      const response = await fetchWrapper.get(backendRoutes.app_global_settings_can_student_register)
      this.canRegister = response.can_register
    },
    async postFormApi() {
      let errorCheck = false

      let student = Object({...this.student})
      student.contactPeople = [
        this.contactPerson1,
        this.contactPerson2
      ]

      let body = {
        student: student,
        payment: this.payment,
        step: this.activeStep
      }

      const response = await fetchWrapper.post(backendRoutes.student_public_register, body).catch((error) => {
        if(error === "course_full") this.$toast.add({severity:'error', summary: this.$t('student_views.public_register_view.messages.error_title'), detail: this.$t('student_views.public_register_view.messages.errors.course_full'), life: 5000})
        else if(error === "workshop_full") this.$toast.add({severity:'error', summary: this.$t('student_views.public_register_view.messages.error_title'), detail: this.$t('student_views.public_register_view.messages.errors.workshop_full'), life: 5000})
        else if(error === "workshop_age") this.$toast.add({severity:'error', summary: this.$t('student_views.public_register_view.messages.error_title'), detail: this.$t('student_views.public_register_view.messages.errors.workshop_age'), life: 5000})
        else if(error === "course_age") this.$toast.add({severity:'error', summary: this.$t('student_views.public_register_view.messages.error_title'), detail: this.$t('student_views.public_register_view.messages.errors.course_age'), life: 5000})
        else if(error === "course_already_registered") this.$toast.add({severity:'error', summary: this.$t('student_views.public_register_view.messages.error_title'), detail: this.$t('student_views.public_register_view.messages.errors.course_already_registered'), life: 5000})
        else if(error === "workshop_already_registered") this.$toast.add({severity:'error', summary: this.$t('student_views.public_register_view.messages.error_title'), detail: this.$t('student_views.public_register_view.messages.errors.workshop_already_registered'), life: 5000})
        else this.$toast.add({severity:'error', summary: this.$t('student_views.public_register_view.messages.error_title'), detail: error, life: 5000});
        errorCheck = true
      })

      if(!errorCheck){
        if(this.activeStep == 1){
          this.isStudentExisting = response.is_student_existing
          if(this.isStudentExisting){
            this.hasActiveInsuranceSubscription = response.has_active_insurance_subscription
            if(!this.hasActiveInsuranceSubscription){
              this.annualInsurancePrice = response.insurance_price
            }
          }else{
            this.annualInsurancePrice = response.insurance_price
          }

          this.activeStep = 2
        }
        else {
          if(this.activeStep >= 2) {
            this.activeStep = 3
            this.allSuccess = true
          }
          if(response == "saved") this.$toast.add({severity:'success', summary: this.$t('student_views.public_register_view.messages.save_success.title'), detail: this.$t('student_views.public_register_view.messages.save_success.content_save'), life: 5000})
          if(response == "saved_with_update_existing") this.$toast.add({severity:'success', summary: this.$t('student_views.public_register_view.messages.save_success.title'), detail: this.$t('student_views.public_register_view.messages.save_success.content_save_with_update_existing'), life: 5000})
        }
      }
    },
    async fetchCourseList() {
      const response = await fetchWrapper.get(backendRoutes.course_list_public, undefined)
      const apiPlatformParser = new ApiPlatformParser(response, ApiPlatformParser.LIST_TYPE)
      this.courses = apiPlatformParser.getData()
      this.courses.forEach((course: Course) => {
        if(course.id && this.urlSelectedCourses.includes(course.id.toString()) && this.student.courseRegistrations)
          this.student.courseRegistrations.push(course)
        course.day = this.$t('global.days.'+course.day)
      })

      this.courses.sort((a, b) => {
        if(!this.student.courseRegistrations) return 0;
        const aInRegistrations = this.student.courseRegistrations.some(course => course.id === a.id);
        const bInRegistrations = this.student.courseRegistrations.some(course => course.id === b.id);
        if (aInRegistrations && !bInRegistrations) return -1;
        if (!aInRegistrations && bInRegistrations) return 1;
        return 0;
      });
    },
    async fetchWorkshopList() {
      const response = await fetchWrapper.get(backendRoutes.workshop_list_public, undefined)
      const apiPlatformParser = new ApiPlatformParser(response, ApiPlatformParser.LIST_TYPE)
      this.workshops = apiPlatformParser.getData()
      this.workshops.forEach((workshop: Workshop) => {
        if(workshop.id && this.urlSelectedWorkShops.includes(workshop.id.toString()) && this.student.workshopRegistrations)
          this.student.workshopRegistrations.push(workshop)
      })

      this.workshops.sort((a, b) => {
        if(!this.student.workshopRegistrations) return 0;
        const aInRegistrations = this.student.workshopRegistrations.some(workshop => workshop.id === a.id);
        const bInRegistrations = this.student.workshopRegistrations.some(workshop => workshop.id === b.id);
        if (aInRegistrations && !bInRegistrations) return -1;
        if (!aInRegistrations && bInRegistrations) return 1;
        return 0;
      });
    },
    getGenderLabel(value: string){
      return this.$t("admin_views.student.item.messages.gender_label."+value)
    },
    getCountryLabel(value: string){
      return this.$t("admin_views.student.item.messages.country_label."+value)
    },
    getRegisterTypeLabel(value: string){
      return this.$t("student_views.public_register_view.messages.register_type_label."+value)
    },
    prev() {
      this.activeStep -= 1
    },
    next() {
      let errorCheck = false
      const form = this.$refs.form as HTMLFormElement

      if(this.activeStep == 1){
        this.tryToGo = 2
        if(!form.reportValidity()) errorCheck = true
        if(isNull(this.student?.birthday)) errorCheck = true
        if(isNull(this.student?.gender)) errorCheck = true
        if(isNull(this.registerType)) errorCheck = true
        if(isNull(this.student?.address?.country)) errorCheck = true
        if((this.registerType == 'course' || this.registerType == 'both') && (isNull(this.student.courseRegistrations) || !this.student.courseRegistrations
            || this.student.courseRegistrations.length === 0)) errorCheck = true
        if((this.registerType == 'workshop' ||this.registerType == 'both') && (isNull(this.student.workshopRegistrations) || !this.student.workshopRegistrations
            || this.student.workshopRegistrations.length === 0)) errorCheck = true

        if(!errorCheck) {
          this.postFormApi()
        }
      }else if(this.activeStep == 2){
        this.tryToGo = 3
        if(!form.reportValidity()) errorCheck = true

        if(!errorCheck) {
          let paymentProcessor = this.$refs.paymentProcessor as typeof PaymentProcessor
          paymentProcessor.processPayment()
        }
      }

      if(errorCheck){
        this.$toast.add({severity:'error', summary: this.$t('global.messages.fields_invalid.title'), detail: this.$t('global.messages.fields_invalid.content'), life: 5000})
      }
    },
    paymentStatusChange(){
      this.activeStep = 3
    },
    paymentCreated(payment: Payment){
      this.payment = payment
    },
  }
})
</script>

<template>
<Card class="page">
  <template #content>
    <img class="logo" v-if="logoDark && themeStore.getTheme === 'dark'" :src="logoDark" alt="logo"/>
    <img class="logo" v-else-if="logoLight && themeStore.getTheme === 'light'" :src="logoLight" alt="logo"/>
    <div class="title_container">
      <h1>{{ $t("student_views.public_register_view.view.title") }}</h1>
    </div>
    <div v-if="!canRegister">
      <p>{{ $t("student_views.public_register_view.view.student_can_not_register") }}</p>
    </div>
    <form v-else ref="form">
      <Stepper v-model:value="activeStep">
        <StepList>
          <Step :value="1" disabled>{{ $t("student_views.public_register_view.view.step1.title") }}</Step>
          <Step :value="2" disabled>{{ $t("student_views.public_register_view.view.step2.title") }}</Step>
          <Step :value="3" disabled>{{ $t("student_views.public_register_view.view.step3.title") }}</Step>
        </StepList>
        <StepPanels>
          <StepPanel :value="1">
            <FormField :label="$t('student_views.public_register_view.view.step1.register_type')" required>
              <Select :options="registerTypeOptions" :optionLabel="getRegisterTypeLabel" v-model="registerType"
                      :invalid="(tryToGo > 1 && isNull(registerType))"/>
            </FormField>
            <FormField :label="$t('student_views.public_register_view.view.step1.courses')" required
                       v-if="registerType == 'course' || registerType == 'both'">
              <ListBox filter v-model="student.courseRegistrations" :options="courses" multiple :filterFields="['name','day','startHour']"
                       :invalid="(tryToGo > 1 && (student.courseRegistrations === undefined || student.courseRegistrations.length === 0))"
                       :filterPlaceholder="$t('student_views.public_register_view.view.step1.search_input')" ref="courseList">
                <template #option="slotProps">
                    {{ slotProps.option.name }} - {{ slotProps.option.day }} - {{slotProps.option.startHour}} /
                  {{slotProps.option.minimumAge}} {{ $t("global.age_suffix") }} - {{slotProps.option.maximumAge}} {{ $t("global.age_suffix") }}
                </template>
                <template #header>
                  <Select v-model="selectFilters" :options="courseSelectFilter" fluid
                          :placeholder="$t('student_views.public_register_view.view.step1.filter')" @update:modelValue="$refs.courseList.filterValue=selectFilters" />
                </template>
                <template #emptyfilter>
                  <span>{{ $t('student_views.public_register_view.view.step1.no_result') }}</span>
                </template>
                <template #empty>
                  <span>{{ $t('student_views.public_register_view.view.step1.no_result') }}</span>
                </template>
              </ListBox>
            </FormField>
            <FormField :label="$t('student_views.public_register_view.view.step1.workshops')" required
                       v-if="registerType == 'workshop' || registerType == 'both'">
              <ListBox filter v-model="student.workshopRegistrations" :options="workshops" multiple :filterFields="['name','startDate', 'endDate']"
                       :invalid="(tryToGo > 1 && (student.workshopRegistrations === undefined || student.workshopRegistrations.length === 0))"
                       :filterPlaceholder="$t('student_views.public_register_view.view.step1.search_input')">
                <template #option="slotProps">
                  {{ slotProps.option.name }} / {{slotProps.option.startDate}} - {{slotProps.option.endDate}} /
                  {{slotProps.option.minimumAge}} {{ $t("global.age_suffix") }} - {{slotProps.option.maximumAge}} {{ $t("global.age_suffix") }} /
                  {{ priceFormat(slotProps.option.price) }}
                </template>
                <template #emptyfilter>
                  <span>{{ $t('student_views.public_register_view.view.step1.no_result') }}</span>
                </template>
                <template #empty>
                  <span>{{ $t('student_views.public_register_view.view.step1.no_result') }}</span>
                </template>
              </ListBox>
            </FormField>
            <h2>Élève</h2>
            <fieldset class="grid">
              <FormField :label="$t('student_views.public_register_view.view.step1.firstname')" required>
                <InputText v-model="student.firstname" required :invalid="(tryToGo > 1 && isNull(student?.firstname))" />
              </FormField>
              <FormField :label="$t('student_views.public_register_view.view.step1.name')" required>
                <InputText v-model="student.name" required :invalid="(tryToGo > 1 && isNull(student?.name))" />
              </FormField>
            </fieldset>
            <fieldset class="grid">
              <FormField :label="$t('student_views.public_register_view.view.step1.gender')" required is50Width>
                <Select v-model="student.gender" :options="genderOptions" :optionLabel="getGenderLabel"
                        :invalid="(tryToGo > 1 && isNull(student.gender))" @update:modelValue="hasEdit = true" :showClear="!isNull(student.gender)"/>
              </FormField>
              <FormField :label="$t('student_views.public_register_view.view.step1.birthday')" required is50Width>
                <DatePicker dateFormat="dd/mm/yy" v-model="student.birthday"
                            showIcon iconDisplay="input" @update:modelValue="hasEdit = true" :maxDate="new Date()" :invalid="(tryToGo > 1 && isNull(student?.birthday))"/>
              </FormField>
            </fieldset>
            <fieldset class="grid">
              <FormField :label="$t('student_views.public_register_view.view.step1.email')" required>
                <InputText v-model="student.email" type="email" required :invalid="(tryToGo > 1 && isNull(student?.email))" />
              </FormField>
              <FormField :label="$t('student_views.public_register_view.view.step1.email2')">
                <InputText v-model="student.email2" type="email"/>
              </FormField>
            </fieldset>
            <fieldset class="grid">
              <FormField :label="$t('student_views.public_register_view.view.step1.phone')">
                <TelInput v-model="student.phone"/>
              </FormField>
              <FormField :label="$t('student_views.public_register_view.view.step1.landline_phone')">
                <TelInput v-model="student.landLinePhone" />
              </FormField>
            </fieldset>
            <FormField :label="$t('student_views.public_register_view.view.step1.medical_history')">
              <Textarea v-model="student.medicalHistory" autoResize :fluid="true" />
            </FormField>
            <h2>Adresse</h2>
            <FormField :label="$t('student_views.public_register_view.view.step1.address1')" required>
              <InputText v-model="student.address.address1" required :invalid="(tryToGo > 1 && isNull(student?.address?.address1))" />
            </FormField>
            <FormField :label="$t('student_views.public_register_view.view.step1.address2')">
              <InputText v-model="student.address.address2" />
            </FormField>
            <fieldset class="grid">
              <FormField :label="$t('student_views.public_register_view.view.step1.postal_code')" required>
                <InputText v-model="student.address.postalCode" required :invalid="(tryToGo > 1 && isNull(student?.address?.postalCode))" />
              </FormField>
              <FormField :label="$t('student_views.public_register_view.view.step1.city')" required>
                <InputText v-model="student.address.city" required :invalid="(tryToGo > 1 && isNull(student?.address?.city))" />
              </FormField>
            </fieldset>
            <FormField :label="$t('student_views.public_register_view.view.step1.country')" required>
              <Select v-model="student.address.country" :options="countryOptions" :optionLabel="getCountryLabel" :invalid="(tryToGo > 1 && isNull(student?.address?.country))" :showClear="!isNull(student?.address?.country)"/>
            </FormField>

            <h2>{{ $t("student_views.public_register_view.view.step1.contact_people_text") }}</h2>
            <h3>{{ $t("student_views.public_register_view.view.step1.contact1") }}</h3>
            <fieldset class="grid">
              <FormField :label="$t('student_views.public_register_view.view.step1.firstname')" :required="isContactPeopleRequired">
                <InputText v-model="contactPerson1.firstname" :required="isContactPeopleRequired" :invalid="(tryToGo > 1 && isContactPeopleRequired && isNull(contactPerson1.firstname))" />
              </FormField>
              <FormField :label="$t('student_views.public_register_view.view.step1.name')" :required="isContactPeopleRequired">
                <InputText v-model="contactPerson1.name" :required="isContactPeopleRequired" :invalid="(tryToGo > 1 && isContactPeopleRequired && isNull(contactPerson1.name))" />
              </FormField>
            </fieldset>
            <fieldset class="grid">
              <FormField :label="$t('student_views.public_register_view.view.step1.email')" is50Width :required="isContactPeopleRequired">
                <InputText :required="isContactPeopleRequired" v-model="contactPerson1.email" type="email" :invalid="(tryToGo > 1 && isContactPeopleRequired && isNull(contactPerson1.email))" />
              </FormField>
              <FormField :label="$t('student_views.public_register_view.view.step1.phone')" is50Width :required="isContactPeopleRequired">
                <TelInput :required="isContactPeopleRequired" v-model="contactPerson1.phone" :invalid="(tryToGo > 1 && isContactPeopleRequired && isNull(contactPerson1.phone))" />
              </FormField>
            </fieldset>
            <h3>{{ $t("student_views.public_register_view.view.step1.contact2") }}</h3>
            <fieldset class="grid">
              <FormField :label="$t('student_views.public_register_view.view.step1.firstname')">
                <InputText v-model="contactPerson2.firstname" />
              </FormField>
              <FormField :label="$t('student_views.public_register_view.view.step1.name')">
                <InputText v-model="contactPerson2.name" />
              </FormField>
            </fieldset>
            <fieldset class="grid">
              <FormField :label="$t('student_views.public_register_view.view.step1.email')" is50Width>
                <InputText v-model="contactPerson2.email" />
              </FormField>
              <FormField :label="$t('student_views.public_register_view.view.step1.phone')" is50Width>
                <TelInput v-model="contactPerson2.phone" />
              </FormField>
            </fieldset>
          </StepPanel>
          <StepPanel :value="2">
            <h2>{{ $t("student_views.public_register_view.view.step2.payment_title") }}</h2>
            <h3>{{ $t("student_views.public_register_view.view.step2.summary_title") }}</h3>
            <Message v-if="this.isStudentExisting" severity="warn">{{ $t("student_views.public_register_view.view.step2.is_existing_student") }}</Message>
            <DataTable :value="paymentTable">
              <Column field="name" :header="$t('student_views.public_register_view.view.step2.name')">
                <template #body="slotProps">
                  <b v-if="slotProps.data.bold">{{ slotProps.data.name }}</b>
                  <span v-else>{{ slotProps.data.name }}</span>
                </template>
              </Column>
              <Column field="price" :header="$t('student_views.public_register_view.view.step2.price')">
                <template #body="slotProps">
                  <b v-if="slotProps.data.bold">{{ typeof slotProps.data.price == "number" ? priceFormat(slotProps.data.price): slotProps.data.price }}</b>
                  <span v-else>{{ typeof slotProps.data.price == "number" ? priceFormat(slotProps.data.price): slotProps.data.price }}</span>
                </template>
              </Column>
            </DataTable>
            <h3 style="margin-top: 2rem">{{ $t('student_views.public_register_view.view.step2.payment_method') }}</h3>
            <PaymentProcessor ref="paymentProcessor" :paymentType="typeRegistration" :paymentItems="paymentItems"
              @paymentCompleted="postFormApi" @paymentStatusChange="paymentStatusChange" @paymentCreated="paymentCreated">
              <template #bankTransferText>
                <p v-html="$t('student_views.public_register_view.view.step2.payment_bank_transfert_infos',{price: priceFormat(totalPrice)})"></p>
              </template>
            </PaymentProcessor>
            <FormField :label="$t('student_views.public_register_view.view.step2.rules_consent')" isCheckBox style="margin-top: 3rem" required>
              <Checkbox v-model="rulesConsent" binary :required="activeStep == 2" :invalid="(tryToGo > 2 && !rulesConsent)"/>
            </FormField>
            <FormField :label="$t('student_views.public_register_view.view.step2.gdpr_consent')" isCheckBox required>
              <Checkbox v-model="gdprConsent" binary :required="activeStep == 2" :invalid="(tryToGo > 2 && !gdprConsent)" />
            </FormField>
            <FormField :label="$t('student_views.public_register_view.view.step2.image_right')" isCheckBox>
              <Checkbox v-model="student.imageRight" binary />
            </FormField>
          </StepPanel>
          <StepPanel :value="3">
            <p v-if="allSuccess">{{ $t("student_views.public_register_view.view.step3.saved_success") }}</p>
            <p v-else>{{ $t("student_views.public_register_view.view.step3.error") }}</p>
          </StepPanel>
        </StepPanels>
      </Stepper>
      <div class="step_buttons_container">
        <Button :style="this.activeStep == 2 ? '': 'visibility: hidden'" :label="$t('student_views.public_register_view.view.previous_button')" severity="secondary"
                icon="pi pi-arrow-left" @click="prev" />
        <Button :style="this.activeStep == 3 ? 'visibility: hidden': ''" :label="$t('student_views.public_register_view.view.next_button')" iconPos="right" severity="secondary"
                icon="pi pi-arrow-right" @click="next" />
      </div>
    </form>
  </template>
</Card>
</template>

<style scoped lang="scss">
.page{
  margin: 1rem auto;
  max-width: 800px;
  .logo{
    height: 3rem;
    margin: 0 auto;
    display: block;
  }
  table{
    width: 100%;
    margin-top: 1rem;
    margin-bottom: 1rem;
    td{
      padding: 0.5rem;
      border: 1px solid white;
    }
  }
  fieldset{
    border: 0;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    flex-wrap: wrap;
    &.grid > *{
      flex-grow: 1;
    }
  }
  .step_buttons_container{
    display: flex;
    justify-content: space-between;
  }
  h2{
    font-size: 1.5rem;
    margin-top: 1rem;
    margin-bottom: 1.2rem;
  }
  h3{
    font-size: 1.2rem;
    margin-top: 1rem;
    margin-bottom: 1.2rem;
  }
  .p-button{
    margin-top: 1rem;
  }
  .title_container{
    display: flex;
    align-items: start;
    justify-content: left;
    margin-bottom: 2rem;
    margin-top: 2rem;
    font-size: 1rem;
    .p-button{
      margin-top: 0;
      margin-left: 1rem;
    }
  }
}
@media screen and (max-width: (800px)) {
  .page{
    margin: 1rem;
  }
}
</style>